/**
 * Hvem som står bak tjenesten.
 *
 * Samlet ett sted fordi de samme opplysningene skal stå i salgsbetingelsene, i
 * bruksvilkårene og etter hvert på kvitteringer — og fordi et organisasjonsnummer
 * som er skrevet av for hånd tre steder, blir feil ett av dem.
 *
 * Ehandelsloven § 8 krever at en tjenesteyter oppgir navn, adresse og
 * organisasjonsnummer «lett, direkte og vedvarende tilgjengelig». Det er ikke en
 * formalitet: det er slik en kunde finner ut hvem de faktisk handler med.
 *
 */

export const SELSKAP = {
  /** Det juridiske navnet, slik det står i Brønnøysundregistrene. */
  navn: 'vardag.no AS',
  /** Det tjenesten heter utad. */
  tjeneste: 'Vår Dag',
  /** Skrevet i grupper på tre, slik Brønnøysund selv gjør det. */
  organisasjonsnummer: '924 542 802',
  /**
   * Et aksjeselskap er registrert i Foretaksregisteret, og
   * foretaksregisterloven § 10-2 krever at det står på forretningsdokumenter –
   * kvitteringer medregnet.
   */
  iForetaksregisteret: true,
  /** Forretningsadresse, ikke postboks – kunden skal vite hvor selskapet holder til. */
  adresse: 'Sevikveien 24',
  postnummerOgSted: '3143 Kjøpmannskjær',
  land: 'Norge',
  epost: 'post@vardag.no',
  nettsted: 'vardag.no',
  /**
   * Om selskapet er registrert i Merverdiavgiftsregisteret.
   *
   * Ikke en detalj: er det `false`, skal prisene ikke si «inkl. mva.»,
   * kvitteringene ikke oppgi mva., og organisasjonsnummeret ikke ha
   * MVA-endelsen. Terskelen er 50 000 kroner i omsetning over en
   * tolvmånedersperiode – passeres den, er dette feltet det første som skal
   * endres.
   *
   * **Fire ting hører sammen med den endringen,** og tre av dem er ikke kode:
   *
   * 1. Speilingen i `apps/api/src/shared/selskap.ts` settes i samme slengen.
   *    `tests/mva.test.ts` feiler hvis bare den ene snus.
   * 2. Regnskapsfører avgjør hvilket salg som er det utløsende, og om
   *    inntektskontoen i Fiken fortsatt er riktig. 3000 er satt opp for et
   *    selskap uten avgift.
   * 3. Kvitteringer utstedt før registreringen blir stående som de er. Det
   *    krever ingenting av oss: refusjonen reverserer avgiften som faktisk ble
   *    fakturert, ikke dagens sats.
   * 4. Prisene står. Avgiften tas innenfra, så kunden merker ingenting og
   *    marginen faller med en femdel. Det er et valg, ikke en følge.
   */
  mvaRegistrert: false,
} as const;

/** Den alminnelige satsen. Speiling av `apps/api/src/shared/selskap.ts`. */
export const MVA_SATS = 0.25;

/**
 * Organisasjonsnummeret slik det skal stå på et salgsdokument.
 *
 * Er selskapet mva-registrert, skal nummeret ha «MVA» bak seg
 * (bokføringsforskriften § 5-1-1). Er det ikke det, skal endelsen *ikke* stå
 * der – den ville sagt at det ble beregnet merverdiavgift.
 */
export function orgnummerForDokument(registrert: boolean = SELSKAP.mvaRegistrert): string {
  return registrert ? `NO ${SELSKAP.organisasjonsnummer} MVA` : SELSKAP.organisasjonsnummer;
}

/**
 * Om en jurist har gått gjennom vilkårene og salgsbetingelsene.
 *
 * Så lenge den er `false`, står det et synlig utkastmerke på begge sidene. Det
 * er ikke en formalitet: et dokument som *ser* ferdig ut, blir lest som om det
 * er det – av kunder, og av oss selv den dagen vi skal svare på en klage.
 *
 * Satt til `true` i oktober 2026, etter andre runde hos juristen: alle tre
 * dokumentene er lest slik de står, med rollebyttet og de fire spørsmålene det
 * skapte. Endres teksten materielt, går flagget tilbake til `false` til den er
 * lest igjen.
 *
 * Selskapsopplysningene over hadde tidligere sin egen sjekk. Den er borte nå
 * som de er fylt ut, og fordi TypeScript ellers vet at sammenligningen alltid
 * er sann. Endres et av feltene til noe tomt, er dette flagget stedet å slå av
 * igjen til det er ordnet.
 */
export const JURIDISK_GJENNOMGAATT = true;

/**
 * Linja under prisen på et pakkekort.
 *
 * Én kilde, fordi det er en påstand om selskapets avgiftsplikt og ikke en
 * formulering: sto «inkl. mva.» på prissiden mens salgsbetingelsene sa at
 * selskapet ikke er mva-registrert, ville ett av stedene vært feil overfor en
 * kunde – og det er den slags som havner hos Forbrukertilsynet.
 */
export function prislinje(registrert: boolean = SELSKAP.mvaRegistrert): string {
  return registrert ? 'Engangsbeløp, inkl. mva.' : 'Engangsbeløp, endelig pris';
}

/**
 * Avsnittet om pris i salgsbetingelsene.
 *
 * Står her og ikke i sida, av samme grunn som linja over: det er en påstand om
 * selskapets avgiftsplikt, og to påstander om det samme er én for mange.
 *
 * Begge utgavene ender på det samme løftet – at prisen du ser, er den du
 * betaler. Det er det kravet til totalpris overfor forbruker handler om, og
 * det holder enten avgiften er der eller ikke. «590 + mva» skal det aldri stå.
 */
export function prisavsnitt(registrert: boolean = SELSKAP.mvaRegistrert): string {
  return registrert
    ? `Prisene er oppgitt inkludert merverdiavgift, som er ${Math.round(MVA_SATS * 100)} prosent. Prisen du ser, er den endelige.`
    : 'Selskapet er ikke registrert i Merverdiavgiftsregisteret, og det beregnes derfor ikke merverdiavgift. Prisen du ser, er den endelige.';
}

/**
 * Datoen dokumentene sist ble endret.
 *
 * Skrives for hånd og ikke hentes fra byggetidspunktet: en dato som flytter seg
 * hver gang noen retter en skrivefeil i en helt annen fil, sier ingenting om når
 * vilkårene faktisk ble endret – og det er nettopp det kunden vil vite.
 */
export const VILKAAR_VERSJON = '2026-09-14';
